import { useCallback, useState, useMemo } from "react";
import TopMenu from "components/OperationsSales/TopMenu";
import AlertIcon from "components/icons/AlertIcon";
import CustomModalAlerts from "components/common/ModalAlerts";
import Bill from "components/common/Bill";
import useOrders from "graphql/useOrders";
import { utils, writeFile } from "xlsx";
import moment from "moment";
import { usePaginatedData } from "helpers/hooks";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "components/common/BottomPageSelectorWithPagination";
import { getDateHeader } from "helpers/dataHelper";
import CheckboxTable from "components/common/CheckboxTable";
import List from "../../components/common/ListWithMoreOptions";
import DocumentPDF from "./DocumentPDF";
import s from "../../styles/Operations.styles";
import { useBusinessContext } from "../../BusinessApp";
import { printDocumentPDF } from "helpers/utils";

const SCAPE_VALUE = "\\";

function fixValueForQuery(value) {
  return value
    .replace(`${SCAPE_VALUE}`, `${SCAPE_VALUE}${SCAPE_VALUE}`)
    .replace("*", `${SCAPE_VALUE}*`);
}

function OperationsSales() {
  const { t } = useTranslation();

  const [confirmationModal, setconfirmationModal] = useState(false);
  const [billToView, setBillToView] = useState(null);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(false);
  const [billType, setBillType] = useState("all");
  const [terminal, setTerminal] = useState("all");
  const [users, setUsers] = useState("all");
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });
  const [selected, setSelected] = useState([]);

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  const { storeId, storeInfo, currencyFormat } = useBusinessContext();

  const { orders, loading: loading1 } = useOrders(storeId, date?.range);


  const onClose = () => {
    setVisible(false);
  };

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }
  const data = useMemo(() => {
    const listFiltered = orders.filter(
      (i) =>
        (billType === "all" ||
          billType === i.type ||
          (billType === "facsimple" &&
            i.type === "fac" &&
            i.subtype === "simple") ||
          (billType === "faccomplete" &&
            i.type === "fac" &&
            i.subtype === "complete")) &&
        (terminal === "all" || terminal === i.invoiceSerial) &&
        (users === "all" || users === i.billingUserName)
    );

    return listFiltered.filter((i) => {
      const regex = new RegExp(fixValueForQuery(search), "i");
      return i.invoiceNumber?.match(regex);
    });
  }, [orders, billType, terminal, users, search]);

  const dataToShow = usePaginatedData(data, pagination, sorting, (item) => [
    <CheckboxTable
      key={item.invoiceNumber}
      name={[`${item.invoiceSequence}-${item.invoiceNumber}`]}
      checked={selected.find((curr) => curr === item._id)}
      setChecked={(value) => {
        if (value) setSelected([...selected, item._id]);
        else {
          const newSelected = [...selected];
          const idx = selected.findIndex((curr) => curr === item._id);
          if (idx >= 0) {
            newSelected.splice(idx, 1);
          }
          setSelected(newSelected);
        }
      }}
    />,

    [t(`orders.types.${item.type}`)],
    item.invoiceSerial,
    moment(item.creationDatetime).format("llll"),
    [item.customerName === null ? "-" : item.customerName],
    [
      item?.payment === "cash"
        ? t("containers.OperationsSales.cash")
        : item?.payment === null
        ? t("containers.OperationsSales.storeCredit")
        : item?.payment === "refund_cash"
        ? t("containers.OperationsSales.refundCash")
        : item?.payment === "store_credit_create"
        ? t("containers.OperationsSales.storeCreditCreated")
        : item?.payment === "card"
        ? t("containers.OperationsSales.creditCard")
        : t("containers.OperationsSales.others"),
    ],
    [currencyFormat(item.total)],
  ]);

  const selectOrders = useMemo(() => {
    if (selected.length === 0) {
      return orders;
    }
    return orders.filter((element) => {
      if (selected.includes(element._id)) {
        return element;
      }
    });
  }, [orders, selected]);


  const dataToPrint = useMemo(() => {
    return selectOrders.map((item) => {
      const taxNames = ["IVA 4%", "IVA 10%", "IVA 21%"];
      const taxes = {
        "IVA 4%": { rate: 0, amount: 0 },
        "IVA 10%": { rate: 0, amount: 0 },
        "IVA 21%": { rate: 0, amount: 0 },
      };
  
      const taxItems = [1, 2, 3, 4];
      taxItems.forEach((i) => {
        const taxName = item[`tax${i}Name`];
        if (taxNames.includes(taxName)) {
          taxes[taxName] = {
            rate: item[`tax${i}`],
            amount: item[`tax${i}Imp`],
          };
        }
      });
  
      return [
        item.type === "fac"
          ? t("containers.OperationsSales.invoice")
          : item.type === "cre"
          ? t("containers.OperationsSales.creditStore")
          : item.type === "vou"
          ? t("containers.OperationsSales.rect")
          : item.type,
        `${item.invoiceSerial}-${item.invoiceSequence}-${item.invoiceNumber}`,
        moment(item.creationDatetime).format("MM/DD/YYYY"),
        parseFloat(item.total || 0),
        parseFloat(item.net || 0),
  
        ...taxNames.flatMap((taxName) => [
          parseFloat(taxes[taxName].rate || 0).toFixed(2),
          parseFloat(taxes[taxName].amount || 0).toFixed(2),
        ]),
  
        item.openUserName || "",
        item.billingUserName || "",
        item.customerName || "",
        `${item.customerAddress || ""} ${item.customerAddress2 || ""}`,
        item.businessName,
        item.payment === "cash"
          ? t("containers.OperationsSales.cash")
          : item.payment === null
          ? t("containers.OperationsSales.storeCredit")
          : item.payment === "refund_cash"
          ? t("containers.OperationsSales.refundCash")
          : item.payment === "store_credit_create"
          ? t("containers.OperationsSales.storeCreditCreated")
          : item.payment === "card"
          ? t("containers.OperationsSales.creditCard")
          : t("containers.OperationsSales.others"),
      ];
    });
  }, [selectOrders, t]);
  


  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t(
          "containers.OperationsSales.OperationsSalesSummaryFor"
        )} ${getDateHeader(date.range.start, date.range.end)}`,
      ],
      [
        t("containers.OperationsSales.type"),
        t("containers.OperationsSales.invoiceNumber"),
        t("containers.OperationsSales.date"),
        t("containers.OperationsSales.total"),
        t("containers.OperationsSales.net"),
        t("containers.OperationsSales.tax4"),
        t("containers.OperationsSales.tax4Base"),
        t("containers.OperationsSales.tax10"),
        t("containers.OperationsSales.tax10Base"),
        t("containers.OperationsSales.tax21"),
        t("containers.OperationsSales.tax21Base"),
        t("containers.OperationsSales.employee"),
        t("containers.OperationsSales.chargedBy"),
        t("containers.OperationsSales.customer"),
        t("containers.OperationsSales.address"),
        t("containers.OperationsSales.CommissionedTo"),
        t("containers.OperationsSales.paymentMethod"),
      ],
      ...dataToPrint,
    ];

    const workbook = utils.book_new();
    const sheetName = t("containers.ReportsItems.Billing");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(
      workbook,
      `${t("containers.OperationsSales.OperationsSalesSummary")}.xlsx`
    );
  }, [date.range.start, date.range.end, dataToPrint, t]);

  const getImageGraph = useCallback(async () => {
    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end),
          dataToPrint,
          currencyFormat
        }}
      />,
      `${t("containers.OperationsSales.OperationsSalesSummary")}.pdf`
    );
  }, [dataToPrint, date.range.end, date.range.start]);

  const loading = loading1;

  return (
    <s.PageContainer>
      <TopMenu
        selectedFilter={filter}
        data={orders}
        setDate={setDate}
        date={date}
        changeSelectedFilter={(v) => setFilter(v)}
        changeSearchValue={(v) => changeSearch(v)}
        setSearch={setSearch}
        billType={billType}
        setBillType={setBillType}
        terminal={terminal}
        setTerminal={setTerminal}
        users={users}
        setUsers={setUsers}
        loading={loading}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={dataToPrint.length === 0}
      />
      <s.ListContainer>
        {/* show no content yet */}
        {!loading && orders.length === 0 && (
          <s.Nocash_managementContainer>
            <s.Nocash_managementMessageCenter>
              <AlertIcon styles={{ display: "block", marginBottom: "7px" }} />
              {t("containers.OperationsSales.notFound")}
            </s.Nocash_managementMessageCenter>
          </s.Nocash_managementContainer>
        )}

        <Bill
          visible={visible}
          onClose={onClose}
          data={billToView}
          storeInfo={storeInfo}
          storeId={storeId}
          setconfirmationModal={setconfirmationModal}
        />
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Spin style={{ color: "#000" }} size="large" tip="Cargando..." />
          </div>
        )}
        {orders?.length > 0 && (
          <List
            width="1000px"
            handleShowDetails={(CashManagementList) => {
              setBillToView(CashManagementList.element);
              setVisible(true);
            }}
            showDetails
            data={dataToShow}
            checkboxOption
            selected={selected}
            setSelected={setSelected}
            columns={[
              {
                title: t("containers.OperationsSales.number"),

                sortable: true,
                columnId: "invoiceNumber",
              },
              {
                title: t("containers.OperationsSales.type"),

                sortable: true,
                columnId: "type",
              },
              {
                title: t("containers.OperationsSales.terminal"),

                sortable: true,
                columnId: "invoiceSerial",
              },
              {
                title: t("containers.OperationsSales.created"),

                sortable: true,
                columnId: "creationDatetime",
              },
              {
                title: t("containers.OperationsSales.client"),
                sortable: true,
                columnId: "customerName",
              },
              {
                title: t("containers.OperationsSales.paymentMethod"),

                sortable: true,
                columnId: "payment",
              },
              {
                title: t("containers.OperationsSales.total"),

                sortable: true,
                columnId: "total",
              },
              {
                title: "",
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{
              gridTemplateColumns: "11fr 10fr 5fr 15fr 10fr 10fr 10fr 5fr",
            }}
          />
        )}
      </s.ListContainer>
      {!loading && (
        <BottomPageSelectorWithPagination
          pagination={pagination}
          setPagination={setPagination}
          length={data.length}
        />
      )}
      <CustomModalAlerts
        visible={confirmationModal}
        width={428}
        height={234}
        centered
        titleAlert={[t("containers.OperationsSales.fileExportedSuccessfully")]}
        info1={[t("containers.OperationsSales.finddownload")]}
        button1={[t("containers.OperationsSales.accept")]}
        onclick1={setconfirmationModal}
      />
    </s.PageContainer>
  );
}

export default OperationsSales;
